import { Injectable, inject, signal, computed, ResourceRef } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { Observable, of, throwError } from "rxjs";
import { tap, catchError, map, switchMap, shareReplay } from "rxjs/operators";
import { GroupedReports, Report, ReportExecuteRequest, ReportExecuteResponse, ReportParameterControl, ReportParameterDefaults } from "@core/types";
import { DownloadService, ReportService } from "@core/services";

@Injectable({ providedIn: "root" })
export class ReportStore {
  //====================================================
  // Dependencies
  //====================================================
  private readonly reportsService = inject(ReportService);
  private readonly downloadService = inject(DownloadService);

  //====================================================
  // Selectors
  //====================================================
  readonly selectedReport = computed(() => this._selectedReport());
  readonly activeReportId = computed(() => this._activeReportId());
  readonly activeReportGroupId = computed(() => this._activeReportGroupId());
  readonly activeSourceCode = computed(() => this._activeSourceCode());

  //====================================================
  // State Management
  //====================================================
  private cache = new Map<string, Observable<any>>();
  private readonly _activeReportId = signal<number>(0);
  private readonly _activeReportGroupId = signal<number>(0);
  private readonly _activeSourceCode = signal<string | null>(null);
  private readonly _selectedReport = signal<Report | null>(null);

  setActiveReport(reportId: number): void {
    this._activeReportId.set(reportId);
    const report = this.getReportById(reportId);
    if (report) this._selectedReport.set(report);
  }

  setReportGroupId(reportGroupId: number): void {
    this._activeReportGroupId.set(reportGroupId);
  }

  setSourceCode(sourceCode: string | null): void {
    this._activeSourceCode.set(sourceCode);
  }

  //====================================================
  // Resources
  //====================================================
  readonly userReports = rxResource({
    stream: () =>
      this.reportsService.getUserReports().pipe(
        map((res) => {
          if (!res.isSuccess || !res.data) {
            throw new Error("Error loading reports");
          }

          const grouped = this.groupReportsByType(res.data);
          const lookup = this.createReportLookup(res.data);

          return { raw: res.data, grouped, lookup };
        }),
      ),
    defaultValue: { raw: [], grouped: [], lookup: new Map() },
  });

  readonly recentReports: ResourceRef<Report[]> = rxResource({
    params: () => this.userReports.value().lookup,
    stream: ({ params: lookup }) => {
      if (lookup.size === 0) return of([]);
      return this.reportsService.getRecentReports().pipe(map((res) => this.mergeReportMetadata(res.data ?? [], lookup)));
    },
    defaultValue: [],
  });

  readonly favoriteReports: ResourceRef<Report[]> = rxResource({
    params: () => this.userReports.value().lookup,
    stream: ({ params: lookup }) => {
      if (lookup.size === 0) return of([]);
      return this.reportsService.getFavoriteReports().pipe(map((res) => this.mergeReportMetadata(res.data ?? [], lookup)));
    },
    defaultValue: [],
  });

  readonly reportDefaults = rxResource({
    params: () => this.activeReportId(),
    stream: ({ params: reportId }) => {
      if (!reportId) return of({} as ReportParameterDefaults);
      return this.getOrFetch(
        `reportDefaults-${reportId}`,
        () => this.reportsService.getReportParameterDefaults(reportId).pipe(map((res) => res.data?.[0] ?? ({} as ReportParameterDefaults))),
        (value) => {
          this._activeReportGroupId.set(value?.reportGroupId ?? 0);
        },
      );
    },
    defaultValue: {} as ReportParameterDefaults,
  });

  readonly reportControls = rxResource({
    params: () => this.activeReportId(),
    stream: ({ params: reportId }) => {
      if (!reportId) return of([] as ReportParameterControl[]);
      return this.getOrFetch(`reportControls-${reportId}`, () => this.reportsService.getReportParameterControls(reportId).pipe(map((res) => res.data ?? [])));
    },
    defaultValue: [],
  });

  //====================================================
  // Write Operations
  //====================================================
  updateFavoriteReports(reportId: number, sortReports?: string): Observable<void> {
    if (sortReports) {
      return this.reportsService.updateFavoriteReports(0, sortReports).pipe(
        tap(() => {
          this.favoriteReports.reload();
        }),
        map(() => void 0),
      );
    }


    const isFav =  this.favoriteReports.value().some((r) => r.reportID === reportId);
    const request$ = isFav ? this.reportsService.deleteFavoriteReport(reportId) : this.reportsService.updateFavoriteReports(reportId);
    return request$.pipe(
      tap(() => {
        this.favoriteReports.reload();
      }),
      map(() => void 0),
    );
     
  }

  updateRecentReports(reportId: number): Observable<void> {
    const report = this.getReportById(reportId);
    if (!report) return throwError(() => new Error("Report not found"));

    return this.reportsService.updateRecentReport(reportId).pipe(
      tap(() => this.recentReports.reload()),
      map(() => void 0),
    );
  }

  //====================================================
  // Report Execution
  //====================================================
  executeReport(reportId: number, params: Record<string, any>): Observable<{ paramsId: string; reportName: string }> {
    const report = this.getReportById(reportId);
    if (!report) return throwError(() => new Error("Report not found"));

    const paramsId = this.generateGuid();
    const reportModel = this.buildReportModel(report, params, "MHTML");
    return this.reportsService.saveReportParameters(paramsId, reportModel).pipe(
      switchMap(() => this.updateRecentReports(reportId).pipe(catchError(() => of(void 0)))),
      map(() => ({
        paramsId,
        reportName: report.reportName,
      })),
    );
  }


  getReportParameters(paramsId: string): Observable<ReportExecuteRequest> {
    return this.reportsService.getReportParameters(paramsId).pipe(map((res) => res.data || {} as ReportExecuteRequest));
  }

  runReport(reportModel: ReportExecuteRequest): Observable<ReportExecuteResponse> {
    return this.reportsService.runReport(reportModel).pipe(map((res) => res.data || {} as ReportExecuteResponse));
  }

  validateReportParameters(reportId: number, params: Record<string, any>): Observable<string[]> {
    const report = this.getReportById(reportId);
    if (!report) return throwError(() => new Error("Report not found"));

    const reportModel = this.buildReportModel(report, params, "MHTML");
    return this.reportsService.validateParameters(reportModel).pipe(map((res) => res.errors ?? []));
  }

  downloadReport(reportId: number, params: any, format: "PDF" | "EXCELOPENXML" | "WordOPENXML" | "TAB"): Observable<void> {
    const report = this.getReportById(reportId);
    if (!report) return throwError(() => new Error("Report not found"));

    const reportModel = this.buildReportModel(report, params, format);

    return this.reportsService.runReport(reportModel).pipe(
      tap((res) => {
        if (!res.isSuccess || !res.data) return;

        const payload = res.data;
        if (!payload.content || !payload.contentType) return;

        const fileName = this.buildFileName(report.reportName, format);

        this.downloadService.downloadBase64File(payload.content, payload.contentType, fileName);
      }),
      map(() => void 0),
    );
  }

  //====================================================
  // Helpers
  //====================================================
  private getReportById(reportId: number): Report | undefined {
    return this.userReports.value().lookup.get(reportId);
  }

  private buildReportModel(report: Report, params: Record<string, any>, format: string) {
    return {
      reportName: report.reportName,
      reportUrl: report.url,
      format: format,
      parameters: params,
    } as ReportExecuteRequest;
  }

  private buildFileName(name: string, format: string): string {
    const extMap: Record<string, string> = {
      PDF: ".pdf",
      EXCELOPENXML: ".xlsx",
      WordOPENXML: ".docx",
      TAB: ".txt",
    };
    return name + (extMap[format] || "");
  }

  private generateGuid(): string {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  private createReportLookup(reports: Report[]): Map<number, Report> {
    return new Map(reports.map((r) => [r.reportID, r]));
  }

  private mergeReportMetadata(items: any[], lookup: Map<number, Report>): Report[] {
    return items.map((item) => {
      const report = lookup.get(item.reportID);
      if (!report) return item;

      return {
        ...item,
        reportName: report.reportName,
        url: report.url,
        reportType: report.reportType,
        customReportControlName: report.customReportControlName,
        lastModified: new Date(item.lastModified + "Z"),
      };
    });
  }

  private groupReportsByType(reports: Report[]): GroupedReports[] {
    const grouped: Record<string, Report[]> = {};

    reports.forEach((r) => {
      const type = r.reportType || "Other Reports";
      grouped[type] = grouped[type] || [];
      grouped[type].push(r);
    });

    return Object.keys(grouped).map((type) => ({
      reportType: type,
      reports: grouped[type],
    }));
  }

  private getOrFetch<T>(key: string, fetchFn: () => Observable<T>, onValue?: (value: T) => void): Observable<T> {
    if (!this.cache.has(key)) {
      const request$ = fetchFn().pipe(
        tap((value) => onValue?.(value)),
        shareReplay({ bufferSize: 1, refCount: true }),
        catchError((err) => {
          this.cache.delete(key);
          return throwError(() => err);
        }),
      );

      this.cache.set(key, request$);
    }

    return this.cache.get(key)! as Observable<T>;
  }
}
